import { useTranslation } from 'react-i18next';
import Button from 'components/button/Button';
import useAppUser from 'hooks/useAppUser';
import useFetchPremiumDbsSiteStatus from 'pages/agencySettings/dbs/useFetchPremiumDbsSiteStatus';
import useFetchAgencyDbsConfig from '../../useFetchAgencyDbsConfig';
import useOpenPremiumDbsEditor from '../../useOpenPremiumDbsEditor';
import {
  PREMIUM_DBS_CONTACT_SALES_URL,
  PREMIUM_DBS_LEARN_MORE_URL,
} from './SiteAccess.constants';
import {
  BannerLinksStyled,
  SiteAccessCardStyled,
  SiteButtonsStyled,
  SiteLabelStyled,
  SiteRowStyled,
  UpgradeBannerStyled,
} from './SiteAccess.styles';
import SiteAccessBannerLoader from './SiteAccessBannerLoader';

const SiteAccessSection = () => {
  const { t } = useTranslation();
  const { agency, agencyUid, isEmployeeManager } = useAppUser();
  const { agencyDbsConfig } = useFetchAgencyDbsConfig();
  const { openPremiumDbsEditor, isOpeningEditor } = useOpenPremiumDbsEditor();

  // Only a manager can be granted editor access, so the premium banner stays hidden for
  // everyone else rather than offering a button the API answers with a 403.
  const canAccessPremium = !!isEmployeeManager;

  const { data: siteStatus, isInitialLoading: isLoadingSiteStatus } =
    useFetchPremiumDbsSiteStatus({
      agencyUid: agencyUid!,
      enabled: canAccessPremium && !!agencyUid,
    });

  // The banner stays put once the site exists; only its copy and its button change, and
  // the same editor-access call sits behind both labels.
  const hasPremiumSite = !!siteStatus;
  // The banner takes the agency name once connected, and could still claim it while the
  // status is in flight, so the row stays quiet rather than handing it back and forth.
  const isManagingPremiumSite = canAccessPremium && hasPremiumSite;
  const showsAgencyNameInRow =
    !isManagingPremiumSite && !(canAccessPremium && isLoadingSiteStatus);
  const bookingWebsiteUrl = agencyDbsConfig?.website;

  return (
    <SiteAccessCardStyled data-testid="site-access-section">
      {canAccessPremium && (
        <UpgradeBannerStyled data-testid="premium-dbs-banner">
          {isLoadingSiteStatus ? (
            <SiteAccessBannerLoader />
          ) : (
            <>
              <div>
                <h4 data-testid="premium-dbs-banner-title">
                  {isManagingPremiumSite
                    ? agency?.name
                    : t('pageDirectBookingEngine.siteAccess.upgradeTitle')}
                </h4>
                <p data-testid="premium-dbs-banner-description">
                  {hasPremiumSite
                    ? t('pageDirectBookingEngine.siteAccess.manageDescription')
                    : t(
                        'pageDirectBookingEngine.siteAccess.upgradeDescription',
                      )}
                </p>
                {!hasPremiumSite && (
                  <BannerLinksStyled>
                    <a
                      data-testid="premium-dbs-learn-more-link"
                      href={PREMIUM_DBS_LEARN_MORE_URL}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {t('pageDirectBookingEngine.siteAccess.learnMore')}
                    </a>
                    <a
                      data-testid="premium-dbs-contact-sales-link"
                      href={PREMIUM_DBS_CONTACT_SALES_URL}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {t('pageDirectBookingEngine.siteAccess.contactSales')}
                    </a>
                  </BannerLinksStyled>
                )}
              </div>
              <Button
                bsStyle="primary"
                data-testid="premium-dbs-editor-button"
                disabled={isOpeningEditor}
                onClick={openPremiumDbsEditor}
              >
                {hasPremiumSite
                  ? t('pageDirectBookingEngine.siteAccess.editAndManage')
                  : t('pageDirectBookingEngine.siteAccess.buildAndPreview')}
              </Button>
            </>
          )}
        </UpgradeBannerStyled>
      )}
      <SiteRowStyled $isInline={isManagingPremiumSite}>
        {showsAgencyNameInRow && (
          <h4 data-testid="site-access-agency-name">{agency?.name}</h4>
        )}
        {isManagingPremiumSite && (
          <SiteLabelStyled data-testid="site-access-standard-description">
            {t('pageDirectBookingEngine.siteAccess.standardSiteDescription')}
          </SiteLabelStyled>
        )}
        <SiteButtonsStyled>
          {!!bookingWebsiteUrl && (
            <Button
              data-testid="booking-website-button"
              href={bookingWebsiteUrl}
              rel="noreferrer"
              target="_blank"
            >
              {t('pageDirectBookingEngine.siteAccess.bookingWebsite')}
            </Button>
          )}
        </SiteButtonsStyled>
      </SiteRowStyled>
    </SiteAccessCardStyled>
  );
};

export default SiteAccessSection;
